(() => {
  const PRODUCT_KEYS = ["yuja", "mist", "barrier", "spf", "night"];

  class ZeeskinRitualFinder extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === "true") return;
      this.dataset.initialized = "true";

      this.questionPanels = [...this.querySelectorAll('[data-zrf-panel="question"]')];
      this.introPanel = this.querySelector('[data-zrf-panel="intro"]');
      this.capturePanel = this.querySelector('[data-zrf-panel="capture"]');
      this.resultsPanel = this.querySelector('[data-zrf-panel="results"]');
      this.progressWrap = this.querySelector('[data-zrf-progress-wrap]');
      this.selectionOrder = new Map();
      this.currentIndex = 0;
      this.products = this.readProducts();

      this.bindEvents();
      if (this.introPanel) this.showIntro(false);
      else this.showQuestion(0, false);
    }

    readProducts() {
      const node = this.querySelector('[data-zrf-products]');
      if (!node) return [];
      try { return JSON.parse(node.textContent).filter(Boolean); }
      catch (error) { console.error("Zeëskin Ritual Finder product JSON error", error); return []; }
    }

    bindEvents() {
      this.querySelector('[data-zrf-start]')?.addEventListener('click', () => {
        this.track('quiz_started', {});
        this.showQuestion(0, true);
      });

      this.questionPanels.forEach((panel, index) => {
        panel.querySelectorAll('[data-zrf-option]').forEach((input) => {
          input.addEventListener('change', () => this.handleOptionChange(panel, input, index));
        });
        panel.querySelector('[data-zrf-next]')?.addEventListener('click', () => this.nextFromQuestion(panel, index));
        panel.querySelector('[data-zrf-back]')?.addEventListener('click', () => this.goBack(index));
      });

      this.querySelector('[data-zrf-retake]')?.addEventListener('click', () => this.resetQuiz());
      this.querySelector('[data-zrf-skip-capture]')?.addEventListener('click', () => this.showResults());

      this.querySelector('[data-zrf-capture-form]')?.addEventListener('submit', (event) => {
        event.preventDefault();
        void this.submitCapture(event.currentTarget);
      });

      this.querySelector('[data-zrf-results-grid]')?.addEventListener('submit', (event) => {
        if (event.target.closest('form[action*="/cart/add"]')) this.track('add_to_cart', { source: 'zeeskin_ritual_finder' });
      });
    }

    handleOptionChange(panel, input, index) {
      const mode = panel.dataset.selectionMode || 'single';
      const maxChoices = Math.max(1, parseInt(panel.dataset.maxChoices || '1', 10));
      const questionId = panel.dataset.questionId || `q${index + 1}`;
      const validation = panel.querySelector('[data-zrf-validation]');
      if (validation) validation.hidden = true;

      if (mode === 'single') {
        panel.querySelectorAll('[data-zrf-option]').forEach((other) => {
          if (other !== input) other.checked = false;
        });
        input.checked = true;
        this.selectionOrder.set(questionId, [input.value]);
        this.trackAnswer(panel, [input]);
        if (panel.dataset.autoAdvance === 'true') window.setTimeout(() => this.nextFromQuestion(panel, index), 220);
        return;
      }

      let order = this.selectionOrder.get(questionId) || [];
      if (input.checked) {
        if (order.length >= maxChoices) {
          input.checked = false;
          if (validation) {
            validation.textContent = `Select up to ${maxChoices}.`;
            validation.hidden = false;
          }
          return;
        }
        if (!order.includes(input.value)) order.push(input.value);
      } else {
        order = order.filter((value) => value !== input.value);
      }
      this.selectionOrder.set(questionId, order);
      const selected = this.getSelectedInputs(panel);
      this.trackAnswer(panel, selected);
    }

    getSelectedInputs(panel) {
      const questionId = panel.dataset.questionId || '';
      const selected = [...panel.querySelectorAll('[data-zrf-option]:checked')];
      const order = this.selectionOrder.get(questionId) || [];
      if (!order.length) return selected;
      return selected.sort((a, b) => order.indexOf(a.value) - order.indexOf(b.value));
    }

    nextFromQuestion(panel, index) {
      const selected = this.getSelectedInputs(panel);
      const validation = panel.querySelector('[data-zrf-validation]');
      if (!selected.length) {
        if (validation) { validation.textContent = 'Please select an option before continuing.'; validation.hidden = false; }
        return;
      }
      if (validation) validation.hidden = true;
      this.currentIndex = index;
      if (index < this.questionPanels.length - 1) this.showQuestion(index + 1);
      else {
        this.track('quiz_completed', {});
        if (this.capturePanel && this.dataset.enableEmailCapture === 'true') this.showCapture();
        else this.showResults();
      }
    }

    goBack(index) {
      if (index <= 0) {
        if (this.introPanel) this.showIntro(true);
        return;
      }
      this.showQuestion(index - 1);
    }

    hideAllPanels() {
      [...this.querySelectorAll('[data-zrf-panel]')].forEach((panel) => panel.hidden = true);
    }

    showIntro(shouldScroll = true) {
      this.hideAllPanels();
      if (this.introPanel) {
        this.introPanel.hidden = false;
        if (shouldScroll) this.bringIntoView(this.introPanel);
      }
      if (this.progressWrap) this.progressWrap.hidden = true;
    }

    showQuestion(index, shouldScroll = true) {
      this.hideAllPanels();
      const panel = this.questionPanels[index];
      if (!panel) return;
      this.currentIndex = index;
      panel.hidden = false;
      if (this.progressWrap) this.progressWrap.hidden = false;
      this.updateProgress(index);
      const back = panel.querySelector('[data-zrf-back]');
      if (back) back.hidden = index === 0 && !this.introPanel;
      if (shouldScroll) this.bringIntoView(panel);
    }

    showCapture() {
      this.hideAllPanels();
      if (this.capturePanel) {
        this.capturePanel.hidden = false;
        this.bringIntoView(this.capturePanel);
      }
      if (this.progressWrap) this.progressWrap.hidden = true;
      this.syncNoteFields();
    }

    bringIntoView(panel) {
      if (!panel || typeof panel.scrollIntoView !== 'function') return;
      window.requestAnimationFrame(() => {
        const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
        panel.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
      });
    }

    async submitCapture(form) {
      const email = form.querySelector('[data-zrf-email]');
      const error = form.querySelector('[data-zrf-email-error]');
      const value = (email?.value || '').trim();
      const valid = /^[^\s@]+@[^\s@]+\.[A-Za-z]{2,}$/.test(value);
      if (!valid) {
        if (error) error.hidden = false;
        email?.focus();
        return;
      }
      if (error) error.hidden = true;
      this.syncNoteFields();
      try {
        await fetch('/contact', { method: 'POST', body: new FormData(form), headers: { Accept: 'text/html' } });
      } catch (error) {
        console.error('Zeëskin Ritual Finder capture failed', error);
      }
      this.showResults();
    }

    syncNoteFields() {
      const box = this.querySelector('[data-zrf-note-fields]');
      if (!box) return;
      box.replaceChildren();
      this.questionPanels.forEach((panel) => {
        const answers = this.getSelectedInputs(panel);
        if (!answers.length) return;
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = `contact[note][${panel.dataset.questionId || 'question'}]`;
        input.value = answers.map((answer) => answer.dataset.label || '').join(' | ');
        box.append(input);
      });
    }

    calculate() {
      const scores = Object.fromEntries(PRODUCT_KEYS.map((key) => [key, 0]));
      const primary = Object.fromEntries(PRODUCT_KEYS.map((key) => [key, 0]));
      const priorities = Object.fromEntries(PRODUCT_KEYS.map((key) => [key, 0]));
      const contributors = Object.fromEntries(PRODUCT_KEYS.map((key) => [key, []]));
      let cap = 5;
      const highCap = Math.max(1, parseInt(this.dataset.highReactivityMax || '3', 10));
      const veryHighCap = Math.max(1, parseInt(this.dataset.veryHighReactivityMax || '2', 10));

      this.questionPanels.forEach((panel) => {
        const selected = this.getSelectedInputs(panel);
        if (!selected.length) return;
        const questionId = panel.dataset.questionId || '';
        const isMulti = panel.dataset.selectionMode === 'multiple';
        const secondWeight = Math.max(0, Math.min(100, parseInt(panel.dataset.secondChoiceWeight || '100', 10))) / 100;

        selected.forEach((input, index) => {
          const weight = isMulti && index > 0 ? secondWeight : 1;
          PRODUCT_KEYS.forEach((key) => {
            const raw = Number(input.dataset[`score${this.camel(key)}`] || 0);
            const delta = raw * weight;
            if (delta > 0) {
              scores[key] += delta;
              contributors[key].push({ signal: input.dataset.profileSignal || input.dataset.label || '', delta, questionId });
              if (questionId === 'q3' && index === 0) primary[key] += raw;
            }
            if (input.dataset[`priority${this.camel(key)}`] === 'true') priorities[key] += 1;
          });

          const answerCap = parseInt(input.dataset.routineCap || '0', 10);
          if (answerCap > 0) cap = Math.min(cap, answerCap);
          if (input.dataset.reactivityLevel === 'high') cap = Math.min(cap, highCap);
          if (input.dataset.reactivityLevel === 'very_high') cap = Math.min(cap, veryHighCap);
        });
      });

      const allowPriorityOnly = this.dataset.allowPriorityOnly === 'true';
      const ranked = this.products
        .filter((product) => product.id && (scores[product.key] > 0 || (allowPriorityOnly && priorities[product.key] > 0)))
        .map((product) => ({ ...product, score: scores[product.key], primary: primary[product.key], priority: priorities[product.key], contributors: contributors[product.key] }))
        .sort((a, b) => (b.score - a.score) || (b.primary - a.primary) || (b.priority - a.priority) || (a.order - b.order));

      return { scores, primary, priorities, contributors, cap, ranked: ranked.slice(0, cap) };
    }

    camel(key) { return key.charAt(0).toUpperCase() + key.slice(1); }

    answerSignals(questionId) {
      const panel = this.questionPanels.find((item) => item.dataset.questionId === questionId);
      if (!panel) return [];
      return this.getSelectedInputs(panel).map((input) => input.dataset.profileSignal || input.dataset.label || '').filter(Boolean);
    }

    showResults() {
      this.hideAllPanels();
      if (!this.resultsPanel) return;
      this.resultsPanel.hidden = false;
      if (this.progressWrap) this.progressWrap.hidden = true;
      const result = this.calculate();
      this.renderSummary(result);
      this.renderRitual(result.ranked);
      this.renderProducts(result.ranked);
      this.bringIntoView(this.resultsPanel);
      this.renderDebug(result);
      this.track('result_viewed', { products: result.ranked.map((item) => item.key), cap: result.cap });
    }

    renderSummary(result) {
      const profile = this.answerSignals('q1').join(' · ') || '—';
      const focus = this.answerSignals('q3').join(' · ') || this.answerSignals('q9').join(' · ') || '—';
      const environment = [...this.answerSignals('q5'), ...this.answerSignals('q6')].join(' · ') || '—';
      this.setText('[data-zrf-profile]', profile);
      this.setText('[data-zrf-focus]', focus);
      this.setText('[data-zrf-environment]', environment);
      const whySignals = [];
      result.ranked.forEach((item) => item.contributors.forEach((entry) => {
        if (entry.signal && !whySignals.includes(entry.signal)) whySignals.push(entry.signal);
      }));
      this.setText('[data-zrf-why-signals]', whySignals.slice(0, 4).join(', ') || 'your selected priorities');
    }

    renderRitual(ranked) {
      const selectedKeys = new Set(ranked.map((item) => item.key));
      const am = this.querySelector('[data-zrf-am-list]');
      const pm = this.querySelector('[data-zrf-pm-list]');
      if (am) am.replaceChildren();
      if (pm) pm.replaceChildren();

      const canonicalAM = ['yuja','mist','spf'];
      const canonicalPM = ['yuja','mist','night'];
      canonicalAM.forEach((key) => { if (selectedKeys.has(key)) this.appendRitualItem(am, key); });
      canonicalPM.forEach((key) => { if (selectedKeys.has(key)) this.appendRitualItem(pm, key); });

      const amColumn = this.querySelector('[data-zrf-am-column]');
      const pmColumn = this.querySelector('[data-zrf-pm-column]');
      if (amColumn) amColumn.hidden = !am || am.children.length === 0;
      if (pmColumn) pmColumn.hidden = !pm || pm.children.length === 0;

      const support = this.querySelector('[data-zrf-barrier-support]');
      if (support) support.hidden = !selectedKeys.has('barrier');
    }

    appendRitualItem(list, key) {
      if (!list) return;
      const product = this.products.find((item) => item.key === key);
      if (!product) return;
      const li = document.createElement('li');
      li.className = 'zrf__ritual-item';

      if (product.image) {
        const media = document.createElement('a');
        media.className = 'zrf__ritual-thumb';
        media.href = product.url || '#';
        const img = document.createElement('img');
        img.src = product.image;
        img.alt = product.title || '';
        img.loading = 'lazy';
        media.append(img);
        li.append(media);
      }

      const wrap = document.createElement('div');
      wrap.className = 'zrf__ritual-copy';
      const a = document.createElement('a');
      a.href = product.url || '#';
      a.textContent = product.title;
      const small = document.createElement('small');
      small.textContent = product.role || '';
      wrap.append(a, small);
      if (product.price) {
        const price = document.createElement('span');
        price.className = 'zrf__ritual-price';
        price.textContent = product.price;
        wrap.append(price);
      }
      li.append(wrap);
      list.append(li);
    }

    renderProducts(ranked) {
      const grid = this.querySelector('[data-zrf-results-grid]');
      const empty = this.querySelector('[data-zrf-empty]');
      if (!grid) return;

      const cards = [...grid.querySelectorAll('[data-zrf-product-preview]')];
      cards.forEach((card) => {
        card.hidden = true;
        card.removeAttribute('data-zrf-result-item');
        const reason = card.querySelector('[data-zrf-product-reason]');
        if (reason) reason.textContent = '';
      });

      if (!ranked.length) {
        if (empty) empty.hidden = false;
        return;
      }

      let shown = 0;
      ranked.forEach((item) => {
        const card = cards.find((node) => node.dataset.productKey === item.key);
        if (!card) return;
        const signals = [...new Set(
          [...item.contributors]
            .sort((a, b) => b.delta - a.delta)
            .map((entry) => entry.signal)
            .filter(Boolean)
        )].slice(0, 3);
        const reason = card.querySelector('[data-zrf-product-reason]');
        if (reason) {
          reason.textContent = `${this.dataset.reasonPrefix || 'Recommended from your answers:'} ${signals.join(', ') || 'your overall ritual priorities'}`;
        }
        card.hidden = false;
        card.dataset.zrfResultItem = '';
        shown += 1;
      });

      if (empty) empty.hidden = shown > 0;
    }

    renderDebug(result) {
      const debug = this.querySelector('[data-zrf-debug]');
      if (!debug || this.dataset.showScoreDebug !== 'true') return;
      const rows = this.products.map((product) => `${product.title}: ${result.scores[product.key] || 0} pts | primary ${result.primary[product.key] || 0} | priority ${result.priorities[product.key] || 0}`);
      debug.textContent = `Result cap: ${result.cap}\n${rows.join('\n')}`;
    }

    updateProgress(index) {
      const total = this.questionPanels.length;
      const percent = total ? ((index + 1) / total) * 100 : 0;
      const bar = this.querySelector('[data-zrf-progress-bar]');
      const label = this.querySelector('[data-zrf-progress-label]');
      const count = this.querySelector('[data-zrf-progress-count]');
      if (bar) bar.style.width = `${percent}%`;
      if (label) label.textContent = this.questionPanels[index]?.dataset.stepperLabel || '';
      if (count) count.textContent = `${index + 1} / ${total}`;
    }

    resetQuiz() {
      this.selectionOrder.clear();
      this.questionPanels.forEach((panel) => panel.querySelectorAll('[data-zrf-option]').forEach((input) => input.checked = false));
      if (this.introPanel) this.showIntro(true); else this.showQuestion(0);
    }

    setText(selector, text) { const node = this.querySelector(selector); if (node) node.textContent = text; }

    trackAnswer(panel, selected) {
      this.track('quiz_answered', {
        question_id: panel.dataset.questionId || '',
        answer_tags: selected.map((input) => input.dataset.answerTag || '').filter(Boolean),
        answer_labels: selected.map((input) => input.dataset.label || '').filter(Boolean)
      });
    }

    track(event, payload) {
      const detail = { event, section_id: this.dataset.sectionId || '', ...payload };
      this.dispatchEvent(new CustomEvent(`zeeskin:${event}`, { detail, bubbles: true }));
      if (this.dataset.enableDataLayer === 'true' && Array.isArray(window.dataLayer)) window.dataLayer.push({ event, ...detail });
    }
  }

  if (!customElements.get('zeeskin-ritual-finder')) customElements.define('zeeskin-ritual-finder', ZeeskinRitualFinder);
})();
